/** @jsx isolateComponent */
import { useState } from 'react';
import { css } from '@emotion/core';
import {
  darkenRGBforAccessibleContrast,
  convertHexColorToRGB,
  convertRGBtoHex,
} from '@zapier/color-utils';
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import type { IconName } from '@zapier/design-system';
import {
  useClickOutside,
  Animation,
  Colors,
  Shadows,
  Typography,
  Avatar,
  Heading,
  Icon,
  IconButton,
  Link,
  Menu,
  MenuItem,
  TagLabel,
  TooltipWrapper,
} from '@zapier/design-system';
import { ServiceIcons } from '../ServiceIcons';
import { useResizeObserver } from './useResizeObserver';

type Service = {
  name: string;
  src: string;
};

type Services = Array<Service>;

type MenuItem = {
  text: string;
  icon: IconName;
  href?: string;
  onClick?: (e: React.MouseEvent, item: MenuItem) => void;
};

type RenderMenuProps = {
  closeMenu: () => void;
};

type Layout = 'auto' | 'card' | 'line';

type Props = {
  /**
   * URL pointing to the author's avatar image.
   */
  authorAvatarUrl: string;
  /**
   * Name of the Zap's author.
   */
  authorName: string;
  /**
   * Function to render the author, which may render above
   * the card or within it, depending on the `layout` prop.
   */
  renderAuthor?: (
    props: Props,
    computedState: ComputedState
  ) => React.ReactNode;
  /**
   * Whether clicking outside of an expanded `ZapCard` closes it.
   */
  canClickOutsideToClose?: boolean;
  /**
   * Color of the card. Typically white, but can be any color
   * to support platform apps' branding. **Note:** `ZapCard`
   * will only be colored when its `layout` is `card`.
   */
  color?: string;
  /**
   * Detailed content to render about the Zap, such as its steps,
   * a description of it, etc. `children` will only render if
   * the current `layout` is `line` (or `auto` and enough space
   * exists to render a `line` `layout`) and the user has expanded
   * `ZapCard` by clicking on it.
   */
  children?: React.ReactNode;
  /**
   * URL to the shared Zap / template.
   */
  href: string;
  /**
   * Optional click handler for when `ZapCard` is clicked.
   * Use for things like event tracking.
   */
  onClick?: (e: React.MouseEvent, computedState: ComputedState) => void;
  /**
   * Optional label to apply to the card. Use for things like "Premium"
   */
  label?: string;
  /**
   * Optional tooltip to render with the label. Use to
   * describe why the `label` has been applied.
   */
  labelTooltip?: string;
  /**
   * Function to render the label. Override to customize how the `label`
   * and `labelTooltip` props render.
   */
  renderLabel?: (props: Props, computedState: ComputedState) => React.ReactNode;
  /**
   * How `ZapCard` should render—as a squarish `card` or as a single `line`.
   * Should generally be left as default `auto` value for responsiveness.
   */
  layout?: Layout;
  /**
   * Array of items to render within the Zap's menu.
   */
  menuItems?: Array<MenuItem>;
  /**
   * Function to render the Zap's menu and its items. Override to
   * customize how the menu renders.
   */
  renderMenu?: (
    props: Props,
    computedState: ComputedState,
    renderMenuProps: RenderMenuProps
  ) => React.ReactNode;
  /**
   * Short additional information about the Zap, such as its category,
   * number of users using it, etc.
   */
  metaInfo?: React.ReactNode;
  /**
   * Function to render meta information about a Zap. Override to
   * customize how meta info renders.
   */
  renderMetaInfo?: (
    props: Props,
    computedState: ComputedState
  ) => React.ReactNode;
  /**
   * Array of services used by the Zap. Used to display service icons.
   */
  services: Services;
  /**
   * Title of the Zap.
   */
  title: string;
};

// Represents state that's computed at render time.
type ComputedState = {
  isExpanded: boolean;
  isMenuOpen: boolean;
  layout: Layout;
  color: string;
  width: number;
  uniqueServices: Array<string>;
};

/* Utility functions
-------------------------------------------------- */

const DEFAULT_COLOR = Colors.white;

const isDefaultColor = (color: Props['color']) => {
  return color === DEFAULT_COLOR;
};

const darkenBackgroundColor = (color: Props['color']) => {
  if (isDefaultColor(color)) {
    return DEFAULT_COLOR;
  }
  // Some of these functions may fail if bad input
  // is provided or if accessible contrast cannot
  // be achieved.
  try {
    return convertRGBtoHex(
      darkenRGBforAccessibleContrast(
        convertHexColorToRGB(color!),
        convertHexColorToRGB(Colors.white)
      ).rgb
    );
  } catch (e) {
    return DEFAULT_COLOR;
  }
};

const hasMenu = (props: Props) => {
  return (props.menuItems || []).length > 0;
};

/* Layout functions and sizes
-------------------------------------------------- */

const CARD_MAX_WIDTH = 630;
const SMALL_LINE_MAX_WIDTH = 770;
const MEDIUM_LINE_MAX_WIDTH = 960;

const isCardLayout = (computedState: ComputedState) => {
  return computedState.layout === 'card';
};

const isLineLayout = (computedState: ComputedState) => {
  return computedState.layout === 'line';
};

const isCardLayoutWidth = (computedState: ComputedState) => {
  return computedState.width <= CARD_MAX_WIDTH;
};

const isSmallLineLayoutWidth = (computedState: ComputedState) => {
  return (
    computedState.width > CARD_MAX_WIDTH &&
    computedState.width <= SMALL_LINE_MAX_WIDTH
  );
};

const isMediumLineLayoutWidth = (computedState: ComputedState) => {
  return (
    computedState.width > SMALL_LINE_MAX_WIDTH &&
    computedState.width <= MEDIUM_LINE_MAX_WIDTH
  );
};

const isLargeLineLayoutWidth = (computedState: ComputedState) => {
  return computedState.width > MEDIUM_LINE_MAX_WIDTH;
};

// Elements change layout based on the size of the element.
// Some move to different locations and some hide. To prevent
// grid gaps from butting up against each other, multiple
// layouts are needed.
const computeLineLayoutGrid = (computedState: ComputedState) => {
  if (isLargeLineLayoutWidth(computedState)) {
    return css`
      grid-template-columns: max-content max-content 1fr max-content max-content max-content max-content;
      grid-template-areas:
        'services title title author label cta menu'
        '.        meta  meta  .      .     .   .';
    `;
  }
  if (isMediumLineLayoutWidth(computedState)) {
    return css`
      grid-template-columns: max-content max-content 1fr max-content max-content max-content;
      grid-template-areas:
        'services title title author label menu'
        '.        meta  meta  .      .     .';
    `;
  }
  return css`
    grid-template-columns: max-content max-content 1fr max-content max-content;
    grid-template-areas:
      'services title  title label menu'
      '.        author meta  .     .';
  `;
};

// Only `card` layouts that don't use the default color
// should be colored.
const shouldBeColored = (computedState: ComputedState) => {
  return isCardLayout(computedState) && !isDefaultColor(computedState.color);
};

// Constant vars that are used throughout `Styles` that
// need to be synced.
const StyleVars = {
  cardPadding: '20px',
  cardHeaderHeight: '40px',
  cardFooterHeight: '55px',
  servicesHeight: '40px',
};

// Reusable styles for vertically centering single-line `title`.
const centerLineCardStyles = css`
  display: flex;
  align-items: center;
  min-height: ${StyleVars.servicesHeight};
`;

const Styles = labelStyles('ZapCard', {
  root: (props: Props, computedState: ComputedState) => [
    css`
      min-width: 270px;
    `,
    // Pad area above card to render absolutely-positioned
    // menu and / or author.
    isCardLayout(computedState) &&
      (hasMenu(props) || props.authorName) &&
      css`
        padding-top: ${StyleVars.cardHeaderHeight};
      `,
  ],
  inner: (computedState: ComputedState) => [
    css`
      position: relative;
      border: 1px solid ${Colors.grey200};
      box-shadow: ${Shadows.elevation5};
      border-radius: 10px;
      background-color: ${DEFAULT_COLOR};
      // Default to undefined.
      --ZapCard-text-color: ;

      // This is the overlay and footer background color. A pseudo
      // element is used to facilitate using CSS Grid for the disparate
      // values for the 'layout' prop.
      &::before {
        pointer-events: none;
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        transition: ${Animation.transitionValue};
      }
    `,
    isCardLayout(computedState) &&
      css`
        --ZapCard-cta-color: ${Colors.grey900};
        --ZapCard-cta-opacity: 0;

        &:hover,
        &:focus-within {
          --ZapCard-cta-color: ${Colors.blue500};
          --ZapCard-cta-opacity: 1;
        }

        &::before {
          height: ${StyleVars.cardFooterHeight};
          border-radius: 0 0 10px 10px;
          background-color: ${Colors.grey000};
        }

        &:hover::before,
        &:focus-within::before {
          background-color: ${Colors.blue100};
        }
      `,
    shouldBeColored(computedState) &&
      css`
        --ZapCard-text-color: ${Colors.white};
        background-color: ${computedState.color};

        &::before {
          background-color: ${Colors.black};
          opacity: 0.25;
        }

        &:hover::before,
        &:focus-within::before {
          background-color: ${Colors.black};
          opacity: 0.55;
        }
      `,
    isLineLayout(computedState) &&
      css`
        &::before {
          top: 0;
          right: 0;
          bottom: 0;
          left: 0;
          border-radius: inherit;
        }

        &:hover::before,
        &:focus-within::before {
          background-color: ${Colors.blue100};
        }
      `,
    isLineLayout(computedState) &&
      computedState.isExpanded &&
      css`
        &::before {
          content: '';
          display: none;
        }
      `,
  ],
  layout: (computedState: ComputedState) => [
    css`
      display: grid;
      position: relative;
    `,
    isCardLayout(computedState) &&
      css`
        label: -card;
        min-height: 210px;
        grid-template-columns: 1fr max-content;
        grid-template-rows: ${StyleVars.servicesHeight} 1fr ${StyleVars.cardFooterHeight};
        gap: 10px;
        align-items: center;
        grid-template-areas:
          'services label'
          'title title'
          'meta cta';
        padding: ${StyleVars.cardPadding} ${StyleVars.cardPadding} 0;
      `,
    isLineLayout(computedState) &&
      css`
        label: -line;
        ${computeLineLayoutGrid(computedState)}
        gap: 2px 20px;
        align-items: start;
        min-height: 80px;
        padding: ${StyleVars.cardPadding};
      `,
  ],
  titleArea: (computedState: ComputedState) => [
    css`
      grid-area: title;
    `,
    isLineLayout(computedState) &&
      css`
        ${centerLineCardStyles}
      `,
  ],
  title: css`
    ${Typography.bodyText900};
    font-weight: 600;
    color: var(--ZapCard-text-color, ${Colors.grey900});
    // All of the properties below this are necessary
    // for line clamping to work. Even though the properties
    // have 'webkit' in them, they work cross-browser.
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
    overflow: hidden;
    resize: none;
  `,
  // Overlay link on top of content to prevent nesting
  // interactive elements.
  link: css`
    display: block;
    position: absolute;
    z-index: 2;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    opacity: 0;
  `,
  ctaArea: (computedState: ComputedState) => [
    css`
      grid-area: cta;
      color: var(--ZapCard-text-color, var(--ZapCard-cta-color));
      position: relative;
      z-index: 3;
    `,
    isCardLayout(computedState) &&
      css`
        // Entire card is clickable; let these events "bleed through."
        pointer-events: none;
        display: grid;
        grid-template-columns: max-content max-content;
        gap: 10px;
        justify-content: end;
        align-items: center;
        transition: ${Animation.transitionValue};
      `,
    isLineLayout(computedState) &&
      css`
        ${centerLineCardStyles};
      `,
  ],
  ctaText: (computedState: ComputedState) => [
    css`
      ${Typography.altText800};
      font-weight: 600;
      color: ${Colors.blue500};
      transition: ${Animation.transitionValue};
    `,
    isCardLayout(computedState) &&
      css`
        color: inherit;
        transition opacity ${Animation.transitionDuration} ${Animation.transitionTimingFunction};
        opacity: var(--ZapCard-cta-opacity);
      `,
  ],
  ctaIcon: (computedState: ComputedState) => [
    isLineLayout(computedState) &&
      css`
        display: none;
      `,
  ],
  menuArea: (computedState: ComputedState) => [
    css`
      position: relative;
      z-index: 4;
    `,
    // Ensure that opened menus render over top of closed menus.
    computedState.isMenuOpen &&
      css`
        z-index: 5;
      `,
    isCardLayout(computedState) &&
      css`
        label: -card;
        display: flex;
        align-items: center;
        position: absolute;
        bottom: 100%;
        right: 10px;
        height: ${StyleVars.cardHeaderHeight};
      `,
    isLineLayout(computedState) &&
      css`
        label: -line;
        grid-area: menu;
        // Tighten gutter
        margin-left: -10px;
        ${centerLineCardStyles};
      `,
  ],
  menu: (computedState: ComputedState) => [
    css`
      position: absolute;
      width: 270px;
      background: ${Colors.white};
      border: 1px solid ${Colors.grey200};
      border-radius: 10px;
      box-shadow: ${Shadows.elevation10};
    `,
    isCardLayout(computedState) &&
      // Subtract a pixel to ensure card coverage.
      css`
        top: calc(100% - 1px);
        right: calc(-10px - 1px);
      `,
    isLineLayout(computedState) &&
      css`
        top: 100%;
        right: calc(-${StyleVars.cardPadding} - 1px);
      `,
  ],
  menuInner: css`
    padding: 10px;
    color: ${Colors.grey700};
  `,
  menuItemInner: css`
    display: grid;
    grid-template-columns: max-content 1fr;
    gap: 8px;
    align-items: center;
  `,
  menuItemText: css`
    overflow: hidden;
    text-overflow: ellipsis;
  `,
  labelArea: (computedState: ComputedState) => [
    css`
      grid-area: label;
      position: relative;
      z-index: 3;
    `,
    isCardLayout(computedState) &&
      css`
        text-align: right;
      `,
    isLineLayout(computedState) &&
      css`
        ${centerLineCardStyles};
      `,
  ],
  servicesArea: () => [
    css`
      grid-area: services;
      position: relative;
      z-index: 3;
    `,
  ],
  servicesTooltipContent: css`
    display: block;
    width: max-content;
    max-width: 250px;
  `,
  metaInfoArea: (computedState: ComputedState) => [
    css`
      grid-area: meta;
      ${Typography.altText900};
      color: var(--ZapCard-text-color, ${Colors.grey900});
      font-size: 12px;
      font-weight: 600;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    `,
    isCardLayout(computedState) &&
      css`
        pointer-events: none;
      `,
    isLineLayout(computedState) &&
      css`
        color: ${Colors.grey500};
      `,
  ],
  authorArea: (computedState: ComputedState) => [
    css`
      ${Typography.altText900};
      gap: 10px;
    `,
    isCardLayout(computedState) &&
      css`
        label: -card;
        display: flex;
        align-items: center;
        position: absolute;
        bottom: 100%;
        left: 10px;
        height: ${StyleVars.cardHeaderHeight};
      `,
    isLineLayout(computedState) &&
      css`
        label: -line;
        grid-area: author;
        ${centerLineCardStyles};
      `,
    isLineLayout(computedState) &&
      isSmallLineLayoutWidth(computedState) &&
      css`
        gap: 5px;
        font-size: 12px;
        min-height: auto;
        position: relative;

        // Simple but somewhat magical way to add middot.
        // Gets the job done, but might refactor if more
        // of these middot separators are needed later.
        &:not(:empty)::after {
          content: '•';
          position: absolute;
          font-size: 16px;
          right: -14px;
          opacity: 0.4;
        }
      `,
  ],
  authorContent: css`
    display: grid;
    grid-template-columns: min-content 1fr;
    gap: inherit;
    align-items: center;
    color: ${Colors.grey700};
  `,
  authorByline: (computedState: ComputedState) => [
    css`
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      max-width: 22ch;
    `,
    isLineLayout(computedState) &&
      css`
        max-width: 15ch;
      `,
  ],
  authorName: css`
    color: ${Colors.black};
    font-weight: 700;
  `,
  summary: css`
    display: block;
    cursor: pointer;
    position: absolute;
    z-index: 2;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    opacity: 0;
  `,
  detailsContent: css`
    position: relative;
    z-index: 3;
    padding: 0 20px 20px;
  `,
});

const MenuButtonColorTheme = {
  custom: {
    base: {
      backgroundColor: 'transparent',
      color: Colors.grey700,
    },
    disabled: {
      backgroundColor: 'transparent',
      color: Colors.grey500,
    },
    hover: {
      backgroundColor: 'transparent',
      color: Colors.blue500,
    },
    focus: {
      backgroundColor: 'transparent',
      color: Colors.blue500,
    },
  },
};

const defaultProps = {
  color: DEFAULT_COLOR,
  layout: 'auto' as Layout,
  menuItems: [],
  renderAuthor: (props: Props, computedState: ComputedState) => {
    if (!props.authorName) {
      return null;
    }
    // This is a simple implementation that doesn't worry about compound names.
    const [first, ...rest] = props.authorName.split(/\s+/);
    const last = rest.pop();
    const lastInitial = last ? `${last[0]}.` : '';
    const name = `${first} ${lastInitial}`.trim();
    return (
      <div aria-label={`by ${props.authorName}`} css={Styles.authorContent}>
        <Avatar
          ariaHidden={true}
          isBlock={true}
          name={props.authorName}
          size={
            isLineLayout(computedState) && isSmallLineLayoutWidth(computedState)
              ? 16
              : 25
          }
          url={props.authorAvatarUrl}
        />
        <p aria-hidden={true} css={Styles.authorByline(computedState)}>
          by <span css={Styles.authorName}>{name}</span>
        </p>
      </div>
    );
  },
  renderLabel: (props: Props, computedState: ComputedState) => {
    const color = shouldBeColored(computedState) ? 'custom' : 'dark';
    const colorTheme = shouldBeColored(computedState)
      ? {
          [color]: {
            backgroundColor: Colors.white,
            color: Colors.grey900,
          },
        }
      : undefined;
    return (
      props.label && (
        <TooltipWrapper
          content={props.labelTooltip}
          position="southeast"
          shouldCorrectPosition={false}
        >
          {() => (
            <TagLabel color={color} colorTheme={colorTheme}>
              {props.label}
            </TagLabel>
          )}
        </TooltipWrapper>
      )
    );
  },
  renderMenu: (
    props: Props,
    computedState: ComputedState,
    { closeMenu }: RenderMenuProps
  ) => {
    if (!hasMenu(props)) {
      return null;
    }
    const items = (props.menuItems || []).map((item) => {
      const onClick = (e: React.MouseEvent) => {
        // Optional chaining not supported by eslint yet.
        // eslint-disable-next-line no-unused-expressions
        item.onClick && item.onClick(e, item);
        closeMenu();
      };
      const icon = item.icon && <Icon name={item.icon} size={24} />;
      return (
        <MenuItem href={item.href} key={item.text} onClick={onClick}>
          <span css={Styles.menuItemInner}>
            {icon}
            <span css={Styles.menuItemText}>{item.text}</span>
          </span>
        </MenuItem>
      );
    });
    return (
      <div css={Styles.menu(computedState)}>
        <Menu ariaLabel="Zap options">
          <div css={Styles.menuInner}>{items}</div>
        </Menu>
      </div>
    );
  },
  renderMetaInfo: (props: Props, computedState: ComputedState) => {
    if (props.metaInfo) {
      return props.metaInfo;
    }
    const truncatedServices = computedState.uniqueServices.slice(0, 3);
    const countDiff =
      computedState.uniqueServices.length - truncatedServices.length;
    return [...truncatedServices, countDiff > 0 && `${countDiff} more`]
      .filter(Boolean)
      .join(' + ');
  },
};

/**
 * Card representing a Zap, Shared Zap, Zap Template, etc.
 */
export const ZapCard = (_props: Props) => {
  const props = { ...defaultProps, ..._props };

  // Menu code
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const closeMenu = () => setIsMenuOpen(false);
  const [setMenuOutsideClickNode] = useClickOutside({
    onClickOutside: closeMenu,
  });
  let menuNode: HTMLDivElement | null = null;
  const setMenuNode = (node: HTMLDivElement) => {
    menuNode = node;
    setMenuOutsideClickNode(node);
  };
  const onClick = (e: React.MouseEvent) => {
    if (!menuNode || !menuNode.contains(e.target as Node)) {
      // Optional chaining not supported by eslint yet.
      // eslint-disable-next-line no-unused-expressions, no-use-before-define
      props.onClick && props.onClick(e, computedState);
    }
  };

  // Expansion code 1
  const [isExpanded, setIsExpanded] = useState(false);
  const [setExpansionOutsideClickNode] = useClickOutside({
    onClickOutside: () => {
      if (props.canClickOutsideToClose) {
        setIsExpanded(false);
      }
    },
  });

  // Dynamic sizing
  const { ref, width } = useResizeObserver();
  const uniqueServices = [
    ...new Set(
      props.services.map((service) => {
        return service.name;
      })
    ),
  ];
  const computedState: ComputedState = {
    isExpanded,
    isMenuOpen,
    layout: props.layout as Layout,
    color: darkenBackgroundColor(props.color),
    width,
    uniqueServices,
  };
  // Reassigned to pass `computedState` into `isCardLayoutWidth`.
  computedState.layout =
    props.layout !== 'auto'
      ? (props.layout as Layout)
      : isCardLayoutWidth(computedState)
      ? ('card' as Layout)
      : ('line' as Layout);

  // Expansion code 2
  const canExpand = props.children && isLineLayout(computedState);

  // Dynamic nodes and content
  const renderedMenu = props.renderMenu(props, computedState, { closeMenu });
  const renderedLabel = props.renderLabel(props, computedState);
  const renderedAuthor = props.renderAuthor(props, computedState);
  const renderedMetaInfo = props.renderMetaInfo(props, computedState);
  const link = (isCardLayout(computedState) || !props.children) && (
    <Link css={Styles.link} href={props.href}>
      {props.title}
    </Link>
  );
  const details = canExpand && (
    <details
      css={Styles.details}
      data-testid="ZapCard__details"
      open={isExpanded}
    >
      <summary
        css={Styles.summary}
        onClick={(e) => {
          e.preventDefault();
          setIsExpanded(!isExpanded);
        }}
      >
        More details
      </summary>
      <div css={Styles.detailsContent}>{props.children}</div>
    </details>
  );
  const cta = (isCardLayout(computedState) ||
    (isLineLayout(computedState) && isLargeLineLayoutWidth(computedState))) && (
    // Add `aria-hidden` to avoid redundant CTAs if it's already handled by `link`.
    // Also add `tabIndex={-1}` to the `Link` since nodes with `aria-hidden` on them
    // shouldn't contain interactive elements.
    <div aria-hidden={!!link} css={Styles.ctaArea(computedState)}>
      <Link
        color={null}
        css={Styles.ctaText(computedState)}
        href={props.href}
        tabIndex={link ? -1 : undefined}
      >
        Try it
      </Link>
      <span css={Styles.ctaIcon(computedState)}>
        <Icon isBlock={true} name="arrowBigRight" size={24} />
      </span>
    </div>
  );
  const servicesTooltipContent = (
    <span css={Styles.servicesTooltipContent}>{uniqueServices.join(', ')}</span>
  );

  // Use eslint-disable below since accessibility is handled elsewhere.
  return (
    <article css={Styles.root(props, computedState)} ref={ref}>
      {/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-static-element-interactions */}
      <div
        css={Styles.inner(computedState)}
        data-testid="ZapCard__inner"
        onClick={onClick}
        ref={setExpansionOutsideClickNode}
      >
        <div css={Styles.layout(computedState)}>
          <div css={Styles.titleArea(computedState)}>
            <Heading css={Styles.title}>{props.title}</Heading>
          </div>
          <div css={Styles.authorArea(computedState)}>{renderedAuthor}</div>
          <div css={Styles.labelArea(computedState)}>{renderedLabel}</div>
          {link}
          {cta}
          <div css={Styles.menuArea(computedState)} ref={setMenuNode}>
            {renderedMenu && (
              <IconButton
                ariaLabel="More options"
                color="custom"
                colorTheme={MenuButtonColorTheme}
                icon="navMoreHoriz"
                iconSize={24}
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                size="xsmall"
              />
            )}
            {isMenuOpen && renderedMenu}
          </div>
          <div css={Styles.servicesArea(computedState)}>
            <TooltipWrapper
              allowMultilineTooltip={true}
              content={servicesTooltipContent}
              position="southwest"
              shouldCorrectPosition={false}
            >
              {() => (
                <ServiceIcons
                  ariaLabel={`Zap uses ${uniqueServices.join(', ')}`}
                  maxServices={3}
                  services={props.services}
                />
              )}
            </TooltipWrapper>
          </div>
          <div css={Styles.metaInfoArea(computedState)}>{renderedMetaInfo}</div>
        </div>
        {details}
      </div>
    </article>
  );
};
